// hooks/useProdStatDashboard.ts
import { useMutation } from '@apollo/client';
import { useProdStat } from '@hooks/graphql/useProdStatData';
import { useDateNavigation } from '@hooks/graphql/useDateNavigation';
import { LOG_EXPORT_AUDIT_EVENT } from 'graphql/audit.gql';
import * as XLSX from 'xlsx';

const useProdStatDashboard = () => {
  const dateNavigation = useDateNavigation();
  const [logExportAudit] = useMutation(LOG_EXPORT_AUDIT_EVENT);

  const {
    data,
    loading,
    isAdmin,
    isSuperAdmin,
    isManager,
    isUser,
    saving,
    handleEdit,
    handleSave,
    saveFeedback,
    asyncApproval,
    setAsyncApproval,
    isRowOriginallyApproved,
    username,
    getBulkApproveHeaderState,
    handleBulkApproveHeaderChange,
    bulkApprovalSelection,
    hasPendingApprovals,
    isSaveDisabled,
    getBulkReadyHeaderState,
    handleBulkReadyHeaderChange,
  } = useProdStat({
    type: 'validated',
    monthYear: dateNavigation.selectedPeriod,
  });

  const handleExportToExcel = () => {
    if (!data || data.length === 0) return;

    const headerRow = ['Vendor Name', 'Meter Name', 'GL', 'Debit', 'Credit', 'Approver', 'Approved At'];

    const dataRows = data.map((row) => {
      const netgen = row.prod_stat_netgen ?? 0;
      const debit = netgen >= 0 ? netgen : '';
      const credit = netgen < 0 ? Math.abs(netgen) : '';
      const approvedAt = row.prod_stat_approved_at
        ? new Date(row.prod_stat_approved_at).toLocaleString()
        : '';
      return [
        row.vendor_name ?? '',
        row.location ?? '',
        row.gl_number ?? '',
        debit,
        credit,
        row.prod_stat_approval_signature ?? '',
        approvedAt,
      ];
    });

    const ws = XLSX.utils.aoa_to_sheet([headerRow, ...dataRows]);
    ws['!cols'] = [
      { wch: 25 },  // Vendor Name
      { wch: 25 },  // Meter Name
      { wch: 15 },  // GL
      { wch: 15 },  // Debit
      { wch: 15 },  // Credit
      { wch: 25 },  // Approver
      { wch: 20 },  // Approved At
    ];

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, ws, 'Prod Stat');

    const filename = `Prod_Stat_Dashboard_${dateNavigation.monthYearLabel.replaceAll(' ', '_')}.xlsx`;
    XLSX.writeFile(workbook, filename);

    logExportAudit({
      variables: { dashboard: 'ProdStatDashboard', monthYear: dateNavigation.selectedPeriod },
    }).catch((err) => console.error('Failed to log export audit event:', err));
  };

  return {
    ...dateNavigation,
    data,
    loading,
    isAdmin,
    isSuperAdmin,
    isManager,
    isUser,
    saving,
    handleEdit,
    handleSave,
    saveFeedback,
    asyncApproval,
    setAsyncApproval,
    isRowOriginallyApproved,
    username,
    handleExportToExcel,
    getBulkApproveHeaderState,
    handleBulkApproveHeaderChange,
    bulkApprovalSelection,
    hasPendingApprovals,
    isSaveDisabled,
    getBulkReadyHeaderState,
    handleBulkReadyHeaderChange,
  };
};

export default useProdStatDashboard;